import {
  Button,
  Loader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelRow,
} from '@teamsnap/teamsnap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { DateFormat, DateService } from 'frontend-toolkit';
import * as React from 'react';
import { useForm } from 'react-hook-form';

// Local Imports
import Modal from 'components/common/modal';
import FormElementHook, {
  useFormDefaults,
} from 'components/form/form-element-hook';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import '../modal-styles.scss';

const CreateElement = FormElementHook as new () => FormElementHook;

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  installment: any;
  orderId: number;
};

export const EditInstallmentModal: React.FC<Props> = ({
  isOpen,
  closeFn,
  installment,
  orderId,
}) => {
  const [dateError, setDateError] = React.useState<string>(undefined);

  const installmentForm = useForm();
  const { handleSubmit, register } = installmentForm;

  const { loading } = useOrderDetails();
  const { updateInstallment, getInstallments } = useOrderDetailsActions();

  const setFormDefaults = React.useCallback(() => {
    if (!installment.scheduledDate) return;

    const amountToDollar = installment.amount
      ? Number(installment.amount.slice(1)).toFixed(2)
      : 0;
    const formValues = {};

    formValues['date'] = DateService.stringToDate(
      installment.scheduledDate,
      DateFormat.YYYY_MM_DD
    );
    formValues['amount'] = amountToDollar;

    Object.entries(formValues).forEach(([k, v]) =>
      installmentForm.setValue(k, v)
    );
  }, [installment.amount, installment.scheduledDate, installmentForm]);

  useFormDefaults(installment, setFormDefaults, [installment.id]);

  const validateDate = (value) => {
    const notEqualToCurrentValue =
      new Date(new Date(value).setUTCHours(0, 0, 0, 0)).getTime() !==
      new Date(
        new Date(installment.scheduledDate).setUTCHours(0, 0, 0, 0)
      ).getTime();

    const greaterThanCurrentDate = DateService.compareDates(
      new Date(value),
      new Date(new Date().setHours(0, 0, 0, 0)),
      1,
      'greater_or_equal'
    );

    // Date must be greater than the current date and cannot equal what is already set.
    if (notEqualToCurrentValue && greaterThanCurrentDate) {
      setDateError(undefined);
      return true;
    } else {
      setDateError(
        'Please select a new scheduled payment date. The selected date cannot be before the current date.'
      );
      return false;
    }
  };

  const resetAndCloseForm = () => {
    installmentForm.reset();
    closeFn();
  };

  const isLoading = loading[LoadingType.UpdateInstallment];

  const submitUpdate = async (values) => {
    if (!validateDate(values.date)) return;

    const convertedDate = DateService.dateToString(
      new Date(values.date),
      DateFormat.YYYY_MM_DD
    );

    try {
      const response = await updateInstallment(
        installment.id,
        false,
        convertedDate
      );

      if (response?.id) {
        reactiveFeedbackMessage({
          message: `Success! Installment ${installment.id} has been updated.`,
          autoclose: true,
          isError: false,
        });
        getInstallments(orderId);
        closeFn();
      } else {
        reactiveFeedbackMessage({
          message: `Unable to update Installment ${installment.id}.`,
          autoclose: true,
          isError: true,
        });
      }
    } catch (error) {
      reactiveFeedbackMessage({
        message: `Oops, something went wrong! Unable to update Installment ${installment.id}.`,
        autoclose: false,
        isError: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeFn}>
      <Panel
        className="edit-installment-modal"
        testId="edit-installment-modal--container"
      >
        <PanelHeader
          className="Panel-header"
          title="Edit Scheduled Payment"
          testId="edit-installment-modal--container-header"
        />
        <PanelBody
          className="Panel-body"
          testId="edit-installment-modal--container-body"
        >
          <PanelRow testId="edit-installment-modal--container-row">
            <div
              className={
                dateError
                  ? 'u-padXs u-spaceEndsXs validation-error'
                  : 'u-padXs u-spaceEndsXs'
              }
              data-testid="edit-installment-modal--date-label"
            >
              <CreateElement
                disabled={isLoading}
                inputRef={register({
                  required: 'Please select a new scheduled payment date.',
                  validate: (value) => validateDate(value),
                })}
                useHookResponse={installmentForm}
                dateProps={(e) => validateDate(e)}
                type="date"
                field="date"
                label={
                  <>
                    Date
                    <span className="u-colorNegative">*</span>
                  </>
                }
              />

              {dateError && <p className="validation-error">{dateError}</p>}
            </div>

            <div
              className="u-padXs u-spaceEndsXs"
              data-testid="edit-installment-amount--label"
            >
              <CreateElement
                disabled={true}
                useHookResponse={installmentForm}
                inputRef={register()}
                type="number"
                field="amount"
                leftIcon={<span style={{ color: '#98A4AE' }}>$</span>}
                label={
                  <>
                    Amount
                    <span className="u-colorNegative">*</span>
                  </>
                }
              />
            </div>

            <div
              className="u-padXs u-spaceEndsXs"
              data-testid="edit-installment-modal--refund-note-label"
            >
              <CreateElement
                disabled={isLoading}
                inputRef={register()}
                useHookResponse={installmentForm}
                type="text"
                field="note"
                label="Note"
                placeholder=""
                autoComplete="false"
              />
            </div>
          </PanelRow>
        </PanelBody>
        <PanelFooter
          mods="u-text-left"
          testId="edit-installment-modal--container-footer"
        >
          <div className="button-list">
            <Button
              isDisabled={isLoading}
              color="default"
              onClick={() => resetAndCloseForm()}
              testId="edit-installment-modal--cancel-button"
            >
              Cancel
            </Button>

            <Button
              isDisabled={isLoading}
              mods="Button--primary"
              type="submit"
              onClick={() => handleSubmit(submitUpdate)()}
              testId="edit-installment-modal--submit-button"
            >
              {isLoading && (
                <Loader type="spin" style={{ width: '13px', height: '13px' }} />
              )}
              {!isLoading && 'Yes, edit payment'}
            </Button>
          </div>
        </PanelFooter>
      </Panel>
    </Modal>
  );
};
